import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useStore } from './store/useStore';
import { usePageWheelPriority } from './hooks/usePageWheelPriority';
import Layout from './components/layout/Layout';
import LoadingSkeleton from './components/ui/LoadingSkeleton';
import TrailerModal from './components/ui/TrailerModal';
import KonamiEasterEgg from './components/ui/KonamiEasterEgg';
import MatrixRainTransition from './components/ui/MatrixRainTransition';

// Core pages — loaded eagerly (most visited)
import Home from './pages/Home';
import Movie from './pages/Movie';
import TvShow from './pages/TvShow';
import Watch from './pages/Watch';
import Search from './pages/Search';
import NotFound from './pages/NotFound';
import ChimeraTrigPage from './pages/ChimeraTrigPage';

// Secondary pages — loaded on demand
const Watchlist = lazy(() => import('./pages/Watchlist'));
const Browse = lazy(() => import('./pages/Browse'));
const Anime = lazy(() => import('./pages/Anime'));
const MoodPicker = lazy(() => import('./pages/MoodPicker'));
const Discover = lazy(() => import('./pages/Discover'));
const Cable = lazy(() => import('./pages/Cable'));
const Sports = lazy(() => import('./pages/Sports'));
const SportsWatch = lazy(() => import('./pages/SportsWatch'));
const NBAHighlights = lazy(() => import('./pages/NBAHighlights'));
const Scan = lazy(() => import('./pages/Scan'));

function LazyRoute({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LoadingSkeleton />}>{children}</Suspense>;
}

function App() {
  const { loadWatchlist, loadContinueWatching } = useStore();
  usePageWheelPriority();

  useEffect(() => {
    loadWatchlist();
    loadContinueWatching();
  }, [loadWatchlist, loadContinueWatching]);

  return (
    <>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="movie/:id" element={<Movie />} />
          <Route path="tv/:id" element={<TvShow />} />
          <Route path="search" element={<Search />} />
          <Route path="watchlist" element={<LazyRoute><Watchlist /></LazyRoute>} />
          <Route path="browse/:type" element={<LazyRoute><Browse /></LazyRoute>} />
          <Route path="anime" element={<LazyRoute><Anime /></LazyRoute>} />
          <Route path="mood" element={<LazyRoute><MoodPicker /></LazyRoute>} />
          <Route path="discover" element={<LazyRoute><Discover /></LazyRoute>} />
          <Route path="cable" element={<LazyRoute><Cable /></LazyRoute>} />
          <Route path="sports" element={<LazyRoute><Sports /></LazyRoute>} />
          <Route path="chimera-trig" element={<ChimeraTrigPage />} />
          <Route path="*" element={<NotFound />} />
        </Route>
        <Route path="/watch/:type/:id" element={<Watch />} />
        <Route path="/scan/:type/:id" element={<LazyRoute><Scan /></LazyRoute>} />
        <Route path="/sports/watch/:league/:gameId" element={<LazyRoute><SportsWatch /></LazyRoute>} />
        <Route path="/sports/highlights/:league/:gameId" element={<LazyRoute><NBAHighlights /></LazyRoute>} />
      </Routes>
      <TrailerModal />
      <MatrixRainTransition />
      <KonamiEasterEgg />
    </>
  );
}

export default App;
